import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getPrintOrder } from '../services/printOrdersAPI';
import type { PrintOrderWithSheetsResponse } from '../schemas/apiResponse.schemas';
import {
  PRINT_EVENT_ORDER_QUERY,
  PRINT_EVENT_RETURN_QUERY,
  PRINT_EVENT_SCANNED_QUERY,
  parseStorageIdPrefixParam,
} from '../utils/scanLabelHandoff';

export interface PrintEventScanResume {
  readonly job: PrintOrderWithSheetsResponse | null;
  readonly scannedPrefix: string | null;
  readonly clear: () => void;
}

function stripPrintEventParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(PRINT_EVENT_ORDER_QUERY);
  next.delete(PRINT_EVENT_SCANNED_QUERY);
  next.delete(PRINT_EVENT_RETURN_QUERY);
  return next;
}

/** Reopen Print Event Handling after Scan Label returns an in-job prefix. */
export function useResumePrintEventScan(): PrintEventScanResume {
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState<PrintOrderWithSheetsResponse | null>(null);
  const [scannedPrefix, setScannedPrefix] = useState<string | null>(null);
  const appliedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const orderId = searchParams.get(PRINT_EVENT_ORDER_QUERY);
    if (!orderId) return;
    const scanned = parseStorageIdPrefixParam(searchParams.get(PRINT_EVENT_SCANNED_QUERY));

    const key = `${orderId}:${scanned ?? ''}`;
    if (appliedKeyRef.current === key) return;
    appliedKeyRef.current = key;

    setSearchParams(stripPrintEventParams(searchParams), { replace: true });

    void getPrintOrder(orderId).then((loaded) => {
      if (!loaded) {
        appliedKeyRef.current = null;
        return;
      }
      setJob(loaded);
      setScannedPrefix(scanned);
    });
  }, [searchParams, setSearchParams]);

  const clear = useCallback((): void => {
    setJob(null);
    setScannedPrefix(null);
    appliedKeyRef.current = null;
  }, []);

  return { job, scannedPrefix, clear };
}
